import React from "react";
import FlexBox from "components/FlexBox/FlexBox";
import { Button } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { DATE_FORMATS } from "constants/configs";
import { IAccessRequestEntity } from "models/AccessRequest.model";
import { useTranslation } from "react-i18next";
import { formatDateStr } from "utils/formatDateStr";

type Args = {
  onClickAccept: (accessRequest: IAccessRequestEntity) => void;
  onClickDecline: (accessRequest: IAccessRequestEntity) => void;
};

export const useIncomingAccessRequestColumns = (args: Args) => {
  const { onClickAccept, onClickDecline } = args;
  const { t } = useTranslation();

  const accessRequestReviewColumns: ColumnDef<IAccessRequestEntity, any>[] = React.useMemo(
    () => [
      {
        header: t("Project ID"),
        enableSorting: true,
        size: 160,
        accessorFn: (originalRow) => originalRow.consentNumber || originalRow.applicationNumber,
      },
      {
        accessorKey: "address",
        header: t("Address"),
        enableSorting: true,
      },
      {
        header: t("Requester"),
        enableSorting: true,
        accessorFn: (originalRow) =>
          `${originalRow.requestUser?.firstName} ${originalRow.requestUser?.lastName}`.trim(),
      },
      {
        header: t("Email"),
        enableSorting: true,
        accessorFn: (originalRow) => originalRow.requestUser?.email,
      },
      {
        header: t("Phone"),
        enableSorting: true,
        accessorFn: (originalRow) => originalRow.requestUser?.phoneNumber,
      },
      {
        accessorKey: "createdDate",
        header: t("Requested on"),
        enableSorting: true,
        size: 160,
        sortingFn: (rowA, rowB) => {
          const dateA = rowA.original.createdDate;
          const dateB = rowB.original.createdDate;

          if (dateA < dateB) {
            return -1;
          }
          return dateA > dateB ? 1 : 0;
        },
        accessorFn: (originalRow) => formatDateStr(originalRow.createdDate, DATE_FORMATS.FULL_DATE),
      },
      {
        header: t("Action"),
        enableSorting: false,
        size: 180,
        cell: (props) => (
          <FlexBox flexDirection={"row"} spacing={2}>
            <Button variant={"outlined"} onClick={() => onClickDecline(props.row.original)}>
              {t("Decline")}
            </Button>
            <Button variant={"contained"} onClick={() => onClickAccept(props.row.original)}>
              {t("Accept")}
            </Button>
          </FlexBox>
        ),
      },
    ],
    [t, onClickAccept, onClickDecline]
  );

  return accessRequestReviewColumns;
};
